/* ============================================================
   Custom dropdown for <select data-custom-select>.

   Why not the native picker: below 16px iOS Safari zooms the whole page
   when a <select> is focused, and every browser draws the open list its
   own way. This keeps the native <select> in the page -- it still holds
   the value, still submits with its form, and still fires the `change`
   events the PDP and the cart already listen for -- and puts a button and
   a listbox in front of it.

   Accessibility follows the ARIA "select-only combobox" pattern: the button
   is named by the field's <label> and announces the current value; the list
   is a listbox with aria-activedescendant. Keys: Enter / Space / arrows open
   it, arrows / Home / End move, Enter / Space pick, Escape closes, Tab
   closes and moves on.

   The list is appended to <body> rather than next to the button, because
   both places this is used sit inside boxes that clip their overflow (the
   PDP's animated frequency row, the side cart's scrolling body).

   Selects are enhanced as they appear, so the side cart -- which replaces
   its whole markup after every change -- needs nothing of its own.
   ============================================================ */

const ENHANCED = new WeakSet();
let uid = 0;
let current = null; // { select, button, list, active }

function optionsOf(select) {
  return Array.from(select.options);
}

function closeList(returnFocus) {
  if (!current) return;
  const { button, list } = current;
  list.remove();
  button.setAttribute('aria-expanded', 'false');
  button.removeAttribute('aria-controls');
  if (returnFocus && button.isConnected) button.focus();
  current = null;
}

function setActive(index) {
  if (!current) return;
  const items = current.list.children;
  if (!items[index]) return;
  if (items[current.active]) items[current.active].classList.remove('is-active');
  items[index].classList.add('is-active');
  // Scrolls the list only. scrollIntoView would also scroll the page when
  // the list sits near an edge, and a page scroll closes the list.
  const item = items[index];
  const list = current.list;
  if (item.offsetTop < list.scrollTop) list.scrollTop = item.offsetTop;
  else if (item.offsetTop + item.offsetHeight > list.scrollTop + list.clientHeight) {
    list.scrollTop = item.offsetTop + item.offsetHeight - list.clientHeight;
  }
  current.list.setAttribute('aria-activedescendant', items[index].id);
  current.active = index;
}

/** Next enabled option from `from` in `step` direction, or `from` itself. */
function nextEnabled(from, step) {
  const opts = optionsOf(current.select);
  for (let i = from + step; i >= 0 && i < opts.length; i += step) {
    if (!opts[i].disabled) return i;
  }
  return from;
}

function choose(index) {
  const { select } = current;
  const option = select.options[index];
  if (!option || option.disabled) return;
  const changed = select.selectedIndex !== index;
  closeList(true);
  if (!changed) return;
  select.selectedIndex = index;
  select.dispatchEvent(new Event('change', { bubbles: true }));
}

function position(button, list) {
  const rect = button.getBoundingClientRect();
  list.style.minWidth = `${rect.width}px`;
  list.style.left = `${rect.left + window.scrollX}px`;
  list.style.top = `${rect.bottom + window.scrollY + 4}px`;

  // Opens upward when there is no room below, e.g. a line at the bottom of
  // the side cart, just above its sticky footer.
  const below = window.innerHeight - rect.bottom;
  if (below < list.offsetHeight + 8 && rect.top > list.offsetHeight + 8) {
    list.style.top = `${rect.top + window.scrollY - list.offsetHeight - 4}px`;
  }
  const overflowRight = rect.left + list.offsetWidth - (window.innerWidth - 8);
  if (overflowRight > 0) {
    list.style.left = `${Math.max(8, rect.left - overflowRight) + window.scrollX}px`;
  }
}

function openList(select, button) {
  if (current && current.select === select) return;
  closeList(false);

  const list = document.createElement('ul');
  list.className = 'gselect-list';
  list.id = `${button.id}-list`;
  list.setAttribute('role', 'listbox');
  list.tabIndex = -1;
  const labelledBy = button.getAttribute('aria-labelledby');
  if (labelledBy) list.setAttribute('aria-labelledby', labelledBy.split(' ')[0]);

  optionsOf(select).forEach((option, i) => {
    const item = document.createElement('li');
    item.className = 'gselect-option';
    item.id = `${list.id}-${i}`;
    item.setAttribute('role', 'option');
    item.setAttribute('aria-selected', option.selected ? 'true' : 'false');
    if (option.disabled) item.setAttribute('aria-disabled', 'true');
    item.textContent = option.text;
    item.addEventListener('click', () => choose(i));
    item.addEventListener('pointermove', () => {
      if (!option.disabled) setActive(i);
    });
    list.append(item);
  });

  document.body.append(list);
  current = { select, button, list, active: -1 };
  position(button, list);

  button.setAttribute('aria-expanded', 'true');
  button.setAttribute('aria-controls', list.id);
  setActive(Math.max(0, select.selectedIndex));
  list.focus({ preventScroll: true });

  list.addEventListener('keydown', event => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActive(nextEnabled(current.active, 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActive(nextEnabled(current.active, -1));
        break;
      case 'Home':
        event.preventDefault();
        setActive(nextEnabled(-1, 1));
        break;
      case 'End':
        event.preventDefault();
        setActive(nextEnabled(select.options.length, -1));
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        choose(current.active);
        break;
      case 'Escape':
        event.preventDefault();
        closeList(true);
        break;
      case 'Tab':
        closeList(false);
        button.focus();
        break;
    }
  });
}

function enhance(select) {
  if (ENHANCED.has(select)) return;
  ENHANCED.add(select);

  uid += 1;
  const button = document.createElement('button');
  button.type = 'button';
  button.id = `gselect-${uid}`;
  // Same classes as the select, so the field keeps the look its section
  // already gives it; the extra class only fixes what a button lacks.
  button.className = `${select.className} gselect-button`;
  button.setAttribute('aria-haspopup', 'listbox');
  button.setAttribute('aria-expanded', 'false');

  const value = document.createElement('span');
  value.className = 'gselect-value';
  button.append(value);

  // The label now names the button: "Delivery frequency for Social, Every
  // 4 weeks". A click on it focuses the button, as it did the select.
  const label = select.id && document.querySelector(`label[for="${CSS.escape(select.id)}"]`);
  if (label) {
    if (!label.id) label.id = `${button.id}-label`;
    label.htmlFor = button.id;
    button.setAttribute('aria-labelledby', `${label.id} ${button.id}`);
  }

  select.classList.add('gselect-native');
  select.tabIndex = -1;
  select.setAttribute('aria-hidden', 'true');
  select.after(button);

  function sync() {
    const option = select.options[select.selectedIndex];
    value.textContent = option ? option.text : '';
    button.disabled = select.disabled;
  }
  sync();
  select.addEventListener('change', sync);
  // The PDP enables and disables its frequency picker with the purchase
  // toggle; `disabled` reflects to the attribute, so this catches it.
  new MutationObserver(sync).observe(select, { attributes: true, attributeFilter: ['disabled'] });

  // Scripts that focus the select after a re-render land on the button.
  select.addEventListener('focus', () => button.focus());

  button.addEventListener('click', () => {
    if (current && current.button === button) closeList(true);
    else openList(select, button);
  });
  button.addEventListener('keydown', event => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      openList(select, button);
    }
  });
}

function scan(root) {
  root.querySelectorAll('select[data-custom-select]').forEach(enhance);
}

scan(document);
new MutationObserver(() => scan(document)).observe(document.body, { childList: true, subtree: true });

document.addEventListener('pointerdown', event => {
  if (!current) return;
  if (current.list.contains(event.target) || current.button.contains(event.target)) return;
  closeList(false);
});

// A list pinned to the page would drift away from its button as the side
// cart or the page scrolls under it, so any scroll outside it closes it.
document.addEventListener(
  'scroll',
  event => {
    if (current && !current.list.contains(event.target)) closeList(false);
  },
  true
);
window.addEventListener('resize', () => closeList(false));
